import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Brand from "@/components/Brand";
import { CaretDown, SignOut, UserCircle, House, Receipt } from "@phosphor-icons/react";

// Shared top bar for public marketing pages (/pro, /salary-benchmark,
// /interview-questions). Behaves as:
//   - Guest: shows compact nav links + "Sign up" CTA.
//   - Signed-in candidate: shows portal shortcuts + avatar dropdown.
//   - Signed-in recruiter/admin: shows a subtle "signed in as X — go to Dashboard"
//     pill so they aren't stranded on a candidate-only landing page.
export default function PublicHeader({ currentPath }) {
    const { user, setUser } = useAuth();
    const nav = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    const isCandidate = user?.role === "candidate";
    const isStaff = user && !isCandidate;

    const links = [
        { to: "/salary-benchmark", label: "Salary", key: "/salary-benchmark" },
        { to: "/interview-questions", label: "Interview", key: "/interview-questions" },
        { to: "/rate-my-cv", label: "Rate CV", key: "/rate-my-cv" },
        { to: "/pro", label: "Pro", key: "/pro" },
    ].filter((l) => l.key !== currentPath);

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        setUser(false);
        setMenuOpen(false);
        nav("/", { replace: true });
    };

    const initials = (user?.name || user?.email || "?")
        .split(/\s+/)
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();

    return (
        <header className="bg-ink text-white sticky top-0 z-40">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="public-header-brand">
                    <Brand variant="light" size="h-8" to={null} />
                </Link>

                <div className="flex items-center gap-3 sm:gap-4 text-xs">
                    {/* Cross-tool nav */}
                    <nav className="hidden sm:flex items-center gap-4">
                        {links.map((l) => (
                            <Link
                                key={l.key}
                                to={l.to}
                                data-testid={`public-header-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="text-white/70 hover:text-white font-bold uppercase tracking-wider"
                            >
                                {l.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Auth-state-aware right side */}
                    {!user && (
                        <>
                            <Link
                                to="/candidate/login"
                                data-testid="public-header-signin"
                                className="text-white/70 hover:text-white font-bold uppercase tracking-wider"
                            >
                                Sign in
                            </Link>
                            <Link
                                to="/candidate/signup"
                                data-testid="public-header-signup"
                                className="bg-cobalt hover:bg-cobalt-dark px-3 sm:px-4 py-1.5 sm:py-2 font-bold uppercase tracking-wider transition-colors"
                            >
                                Sign up
                            </Link>
                        </>
                    )}

                    {isCandidate && (
                        <div className="relative">
                            <button
                                onClick={() => setMenuOpen((s) => !s)}
                                data-testid="public-header-user-menu"
                                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 pl-2 pr-2.5 py-1.5 transition-colors"
                            >
                                <span className="w-6 h-6 bg-splash text-ink font-heading font-black text-[10px] flex items-center justify-center">
                                    {initials || "U"}
                                </span>
                                <span className="hidden sm:inline font-bold uppercase tracking-wider text-white/90 text-[10px]">
                                    {(user.name || user.email || "").split(" ")[0]}
                                </span>
                                <CaretDown size={11} weight="bold" className="text-white/60" />
                            </button>
                            {menuOpen && (
                                <>
                                    <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setMenuOpen(false)}
                                        aria-hidden
                                    />
                                    <div className="absolute right-0 top-full mt-2 min-w-[200px] bg-white text-ink shadow-xl z-20 border border-[#E5E5E5]" data-testid="public-header-menu">
                                        <p className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                                            Signed in as
                                        </p>
                                        <p className="px-4 pb-3 text-xs text-ink truncate max-w-[240px]">
                                            {user.email}
                                        </p>
                                        <div className="border-t border-[#F5F5F5]" />
                                        <Link
                                            to="/candidate"
                                            onClick={() => setMenuOpen(false)}
                                            data-testid="public-header-goto-dashboard"
                                            className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-splash/10"
                                        >
                                            <House size={14} weight="bold" /> Dashboard
                                        </Link>
                                        <Link
                                            to="/candidate/profile"
                                            onClick={() => setMenuOpen(false)}
                                            data-testid="public-header-goto-profile"
                                            className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-splash/10"
                                        >
                                            <UserCircle size={14} weight="bold" /> Profile
                                        </Link>
                                        <Link
                                            to="/candidate/subscriptions"
                                            onClick={() => setMenuOpen(false)}
                                            data-testid="public-header-goto-subscriptions"
                                            className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-splash/10"
                                        >
                                            <Receipt size={14} weight="bold" /> My purchases
                                        </Link>
                                        <Link
                                            to="/candidate/referrals"
                                            onClick={() => setMenuOpen(false)}
                                            data-testid="public-header-goto-referrals"
                                            className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-splash/10"
                                        >
                                            <UserCircle size={14} weight="bold" /> Referrals
                                        </Link>
                                        <div className="border-t border-[#F5F5F5]" />
                                        <button
                                            onClick={logout}
                                            data-testid="public-header-logout"
                                            className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-splash/10 text-[#525252]"
                                        >
                                            <SignOut size={14} weight="bold" /> Sign out
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {isStaff && (
                        <Link
                            to="/dashboard"
                            data-testid="public-header-staff-dashboard"
                            className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 px-3 py-1.5 font-bold uppercase tracking-wider text-white/90 text-[10px] transition-colors"
                            title={`Signed in as ${user.role}. Return to your dashboard.`}
                        >
                            <House size={11} weight="bold" /> Dashboard
                        </Link>
                    )}
                </div>
            </div>
        </header>
    );
}
